import { forwardRef, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { FunctionComponent, ChangeEvent, PropsWithoutRef, Ref } from 'react';

import type { ForwardProps } from '../../types';
import ComboBox, { type ComboBoxProps } from '../ComboBox';
import Icon, {
  icons as budIconNames,
  streamlineIcons as streamlineIconNames,
  type IconProps
} from '../Icon';
import type { MenuProps, MenuItemProps } from '../Menu';
import { menuHelpers } from '../Menu';
import { useI18n } from '../../hooks';
import { createStringMatcher } from '../../utils';

export interface IconPickerProps
  extends Pick<
      ComboBoxProps,
      | 'id'
      | 'label'
      | 'labelHidden'
      | 'required'
      | 'disabled'
      | 'readOnly'
      | 'name'
      | 'status'
      | 'info'
      | 'placeholder'
      | 'autoFocus'
      | 'value'
    >,
    Pick<MenuProps, 'scrollAt'> {
  /** Callback fired when input loses focus. */
  onBlur?: (iconName: string) => void;
  /** Current selection for the input. */
  currentSelection: string;
  /** Callback fired when user chooses icon from the list of icons. */
  onSelect: (iconName: string) => void;
  /**
   * The set of icons to pick from.
   * @default 'budicon'
   */
  set?: IconProps['set'];
}

const getFilteredItems = (iconList: MenuItemProps[], filterValue: string) => {
  if (!filterValue) return iconList;

  const regExp = createStringMatcher(filterValue);
  return iconList.filter(({ primary }) => regExp.test(primary));
};

const pageSize = 20;

const IconPicker: FunctionComponent<IconPickerProps & ForwardProps> = forwardRef(
  function IconPicker(props: PropsWithoutRef<IconPickerProps>, ref: Ref<HTMLInputElement>) {
    const { currentSelection, scrollAt, onSelect, onBlur, set = 'budicon', ...restProps } = props;

    const iconList: MenuItemProps[] = useMemo(() => {
      const icons = set === 'budicon' ? budIconNames : streamlineIconNames;
      return icons.map(icon => {
        return {
          id: icon,
          primary: icon,
          visual: <Icon set={set} name={icon} />,
          selected: false
        };
      });
    }, [set]);

    const [items, setItems] = useState<MenuProps['items']>(() => iconList.slice(0, pageSize));
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [filterValue, setFilterValue] = useState('');
    const t = useI18n();
    const loadMoreTimeoutId = useRef<ReturnType<typeof setTimeout>>();

    const resetItems = useCallback(() => {
      const filteredItems = getFilteredItems(iconList, filterValue);
      setItems(
        menuHelpers.selectItem(filteredItems.slice(0, pageSize), currentSelection, 'single-select')
      );
      setHasMore(filteredItems.length > pageSize);
    }, [filterValue, currentSelection, iconList]);

    const selectedItem = useMemo(
      () =>
        currentSelection
          ? {
              items: {
                id: currentSelection,
                text: currentSelection
              }
            }
          : undefined,
      [currentSelection]
    );

    useEffect(() => {
      resetItems();
    }, [filterValue, currentSelection, iconList, set]);

    useEffect(() => {
      return () => {
        if (loadMoreTimeoutId.current) clearTimeout(loadMoreTimeoutId.current);
      };
    }, []);

    const loadMore = useCallback(() => {
      if (!isLoading) {
        setIsLoading(true);
        if (loadMoreTimeoutId.current) clearTimeout(loadMoreTimeoutId.current);
        loadMoreTimeoutId.current = setTimeout(() => {
          setIsLoading(false);
          setItems(prevItems => {
            const newItems = menuHelpers.mapTree(
              getFilteredItems(iconList, filterValue).slice(
                prevItems.length,
                prevItems.length + pageSize
              ),
              item => ({
                ...item,
                selected: item.items ? undefined : currentSelection === item.id
              })
            );

            if (newItems.length === 0) setHasMore(false);

            return menuHelpers.appendTo(prevItems, newItems);
          });
        }, 500);
      }
    }, [filterValue, isLoading]);

    const selectItem = useCallback(
      (iconName: string) => {
        setFilterValue('');
        onSelect(iconName);
      },
      [onSelect]
    );

    const clearSelection = useCallback(() => {
      onSelect('');
      setItems(cur =>
        menuHelpers.mapTree(cur, item => {
          return {
            ...item,
            selected: false
          };
        })
      );
    }, []);

    return (
      <ComboBox
        {...restProps}
        ref={ref}
        selected={selectedItem}
        value={filterValue}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (!e.target.value) clearSelection();
          setFilterValue(e.target.value);
        }}
        onBlur={selected => {
          setFilterValue('');
          onBlur?.(Array.isArray(selected) ? selected[0]?.id : selected?.id ?? currentSelection);
        }}
        menu={{
          items,
          onItemClick: selectItem,
          scrollAt,
          accent: filterValue ? createStringMatcher(filterValue) : undefined,
          emptyText: t('no_items'),
          loading: isLoading,
          loadMore: hasMore ? loadMore : undefined
        }}
        visual={currentSelection ? <Icon set={set} name={currentSelection} /> : undefined}
      />
    );
  }
);

export default IconPicker;
